import { supabase } from './supabase';
import type { PropertyReportData, CreatePropertyReportResult, VisitComment } from '../types/types';

// Share helpers (WhatsApp / email / system share / clipboard) are shared with
// the vitrina flow — reuse them rather than duplicating.
export {
  openWhatsApp,
  openEmail,
  openSystemShare,
  isWebShareSupported,
  prepareWhatsAppWindow,
  copyVitrinaUrl as copyToClipboard,
} from './vitrina-queries';

/**
 * Create (or refresh) the public seller report token for a property.
 * Re-sharing the same property returns the SAME token with a refreshed expiry.
 */
export async function createPropertyReport(
  propertyId: string,
  message?: string,
): Promise<CreatePropertyReportResult> {
  if (!supabase) throw new Error('Supabase not initialized');

  const { data, error } = await supabase.rpc('create_property_report', {
    p_property_id: propertyId,
    p_message: message ?? null,
  });

  if (error) throw error;
  return data as CreatePropertyReportResult;
}

/**
 * Fetch a sanitized seller report by its public token (UUID). Returns null when
 * the token is missing or expired. Callable anonymously (SECURITY DEFINER RPC).
 */
export async function fetchPropertyReport(token: string): Promise<PropertyReportData | null> {
  if (!supabase) throw new Error('Supabase not initialized');

  const { data, error } = await supabase.rpc('fetch_property_report', {
    p_token: token,
  });

  if (error) throw error;
  if (!data || (data as any).error === 'not_found') return null;
  return data as PropertyReportData;
}

/** Build the public report URL the agent shares with the property owner. */
export function buildReportUrl(token: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/reporte/${token}`;
}

// ── Comentarios de Visita (owner-facing visit feedback) ──────────────────────

/** List the property's visit comments (agency-scoped via RLS), newest first. */
export async function fetchVisitComments(propertyId: string): Promise<VisitComment[]> {
  if (!supabase) throw new Error('Supabase not initialized');

  const { data, error } = await supabase
    .from('property_visit_comments')
    .select('id, comment, visited_at, created_at')
    .eq('property_id', propertyId)
    .eq('is_deleted', false)
    .order('visited_at', { ascending: false });

  if (error) throw error;
  return (data || []) as VisitComment[];
}

/** Add a visit comment (date/time + text). Validates agency ownership server-side. */
export async function addVisitComment(
  propertyId: string,
  comment: string,
  visitedAt?: string,
): Promise<VisitComment> {
  if (!supabase) throw new Error('Supabase not initialized');

  const { data, error } = await supabase.rpc('add_visit_comment', {
    p_property_id: propertyId,
    p_comment: comment,
    p_visited_at: visitedAt ?? null,
  });

  if (error) throw error;
  return data as VisitComment;
}

/** Soft-delete a visit comment (RLS restricts to the caller's agency). */
export async function deleteVisitComment(commentId: string): Promise<void> {
  if (!supabase) throw new Error('Supabase not initialized');

  const { error } = await supabase
    .from('property_visit_comments')
    .update({ is_deleted: true })
    .eq('id', commentId);

  if (error) throw error;
}
